import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, highlights } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(
          typeof serverMsg === 'string' && serverMsg.length > 0
            ? serverMsg
            : '提交失败，请稍后重试或直接联系我们。'
        );
        setFormStatus('error');
      }
    } catch {
      setFormError('网络异常，请检查连接后重试。');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        {/* Brand Intro */}
        <section className="w-full px-6 md:px-10 py-14 md:py-18 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs md:text-sm text-primary-600 tracking-[0.2em] uppercase font-label mb-3">
              About Us
            </p>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-6">
              关于精品八区
            </h1>
            <div className="prose-custom">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                <strong className="text-foreground-900">精品八区</strong>创立于2018年，是国内领先的高端精选生活社区品牌。
                我们致力于为追求卓越的高净值人群打造一站式的品质生活解决方案，将<strong>精品居住</strong>、
                <strong>优质服务</strong>与<strong>专属会员权益</strong>深度融合，重新定义当代精英的生活方式。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                八年来，精品八区以"品质为先、服务至上"为核心理念，在北京、上海、深圳、杭州、成都五大核心城市
                构建了高端社区网络，汇聚了超过500位来自科技、金融、文化、艺术等各领域的精英会员。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                我们深知，真正的品质生活不仅在于居住空间的卓越，更在于生活方式的全方位升级。因此，
                精品八区不仅提供精心甄选的精品住宅资源，更打造了涵盖管家服务、健康管理、艺术鉴赏、投资顾问、
                精英社交等在内的完整服务体系。每一项服务都经过严格的质量把控，每一个细节都力求超越您的期待。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                展望未来，精品八区将继续深耕高端生活服务领域，以创新驱动服务升级，以专业铸就品牌价值。
                我们期待与更多志同道合的会员一起，共同书写精品生活的全新篇章。
              </p>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-900">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {highlights.map((item, i) => (
                <div key={i} className="text-center">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">
                    {item.value}
                  </p>
                  <p className="text-sm font-semibold text-background-300 mb-1">{item.label}</p>
                  <p className="text-xs text-background-500">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="w-full px-6 md:px-10 py-14 md:py-18 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs md:text-sm text-accent-600 tracking-[0.2em] uppercase font-label mb-3">
                Reputation
              </p>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                品牌口碑
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500">
                每一位会员的认可，都是精品八区最宝贵的勋章
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm text-primary-500" />
                    ))}
                  </div>
                  <blockquote className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-6 md:px-10 py-14 md:py-18 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs md:text-sm text-secondary-600 tracking-[0.2em] uppercase font-label mb-3">
                Contact Us
              </p>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                联系我们
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500">
                期待与您交流，精品八区团队将竭诚为您服务
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="text-center p-5 rounded-lg bg-background-100">
                <div className="w-10 h-10 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-phone-line text-lg text-primary-600" />
                </div>
                <p className="text-sm font-semibold text-foreground-900 mb-1">客服热线</p>
                <p className="text-sm text-foreground-500">400-888-8888</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100">
                <div className="w-10 h-10 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-mail-line text-lg text-accent-600" />
                </div>
                <p className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</p>
                <p className="text-sm text-foreground-500">contact@jingpin8qu.com</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100">
                <div className="w-10 h-10 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                  <i className="ri-map-pin-line text-lg text-secondary-600" />
                </div>
                <p className="text-sm font-semibold text-foreground-900 mb-1">总部地址</p>
                <p className="text-sm text-foreground-500">北京市朝阳区建国路88号</p>
              </div>
            </div>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              action="https://readdy.ai/api/form/d9rcnisnlsngrm4lma90"
              method="POST"
              className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/60"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  电话
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的联系电话"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="inquiry_type" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  咨询类型
                </label>
                <select
                  id="inquiry_type"
                  name="inquiry_type"
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                >
                  <option value="">请选择咨询类型</option>
                  <option value="membership">会员申请</option>
                  <option value="residence">精品居住咨询</option>
                  <option value="service">服务咨询</option>
                  <option value="visit">预约参观</option>
                  <option value="cooperation">商务合作</option>
                  <option value="other">其他</option>
                </select>
              </div>

              <div className="mb-5">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {/* Honeypot */}
              <div className="contact-honeypot">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full md:w-auto px-8 py-3 text-sm font-medium whitespace-nowrap cursor-pointer rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200"
              >
                提交咨询
              </button>

              {formStatus === 'success' && (
                <div className="mt-4 p-3 rounded-md bg-green-50 border border-green-200">
                  <p className="text-sm text-green-700">
                    感谢您的留言！我们会在1-2个工作日内与您联系，请留意您的邮箱和电话。
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于精品八区',
            description:
              '精品八区创立于2018年，是国内领先的高端精选生活社区品牌，提供精品居住、优质服务与专属会员权益一站式解决方案。',
            url: 'https://www.jingpin8qu.com/about',
            inLanguage: 'zh-CN',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jingpin8qu.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '精品八区',
            url: 'https://www.jingpin8qu.com/',
            description: '高端精选生活社区，汇聚精品居住、优质服务与专属会员权益。',
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'CN',
              addressRegion: '北京',
              addressLocality: '北京市',
              streetAddress: '朝阳区建国路88号',
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+86-400-888-8888',
              contactType: 'customer service',
              email: 'contact@jingpin8qu.com',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />
    </>
  );
}